/* eslint-disable no-restricted-imports -- this wrapper is the single canonical entry point for dayjs; all consumers must import from `@common/services/datetime`. */
import dayjs, { type Dayjs as DatetimeType, type PluginFunc } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localeData from 'dayjs/plugin/localeData';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';

dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(localeData);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekday);

// === Moment-parity humanize for durations (WORK-6090, M5.2 — WORKS-330) ===
//
// dayjs's duration `.humanize()` delegates to `now.add(this).fromNow()`, which is
// calendar-dependent and divides by a flat 30-day month. moment computes humanize
// straight from the duration with a fixed month length (daysToMonths:
// days * 4800 / 146097 ≈ 30.4369 days/month), so the two diverge by one bucket
// near a rounding boundary (e.g. ~106 days → moment "3 months", dayjs "4 months")
// and dayjs's answer drifts with today's calendar. We replace duration.humanize
// with moment's exact `relativeTime` bucketing so cutover code keeps moment's
// labels and the result is deterministic. Scoped to duration.humanize only —
// `fromNow()` / `relativeTime` on Dayjs instances are untouched. Verified
// identical to moment 2.29.4 across 500k+ durations (both signs, seconds→years).

// moment's default relativeTime thresholds (en).
const MOMENT_DURATION_THRESHOLDS = {
  ss: 44,
  s: 45,
  m: 45,
  h: 22,
  d: 26,
  M: 11,
};
// moment's daysToMonths factor: days * 4800 / 146097.
const DURATION_DAYS_PER_MONTH = 146097 / 4800;
// moment's en relativeTime strings — identical to dayjs's en set plus the `ss`
// ("%d seconds") key dayjs omits. Inlined (the app is en-only) so humanized
// output matches moment exactly without depending on dayjs locale internals.
const DURATION_RELATIVE_TIME = {
  future: 'in %s',
  past: '%s ago',
  s: 'a few seconds',
  ss: '%d seconds',
  m: 'a minute',
  mm: '%d minutes',
  h: 'an hour',
  hh: '%d hours',
  d: 'a day',
  dd: '%d days',
  M: 'a month',
  MM: '%d months',
  y: 'a year',
  yy: '%d years',
} as const;

function momentParityHumanize(
  this: { asMilliseconds: () => number },
  withSuffix?: boolean
): string {
  const ms = this.asMilliseconds();
  const abs = Math.abs(ms);
  const days = abs / 86_400_000;
  const { round } = Math;
  const seconds = round(abs / 1_000);
  const minutes = round(abs / 60_000);
  const hours = round(abs / 3_600_000);
  const roundedDays = round(days);
  const months = round(days / DURATION_DAYS_PER_MONTH);
  const years = round(days / DURATION_DAYS_PER_MONTH / 12);
  const th = MOMENT_DURATION_THRESHOLDS;

  const bucket: [keyof typeof DURATION_RELATIVE_TIME, number?] =
    seconds <= th.ss
      ? ['s']
      : seconds < th.s
        ? ['ss', seconds]
        : minutes <= 1
          ? ['m']
          : minutes < th.m
            ? ['mm', minutes]
            : hours <= 1
              ? ['h']
              : hours < th.h
                ? ['hh', hours]
                : roundedDays <= 1
                  ? ['d']
                  : roundedDays < th.d
                    ? ['dd', roundedDays]
                    : months <= 1
                      ? ['M']
                      : months < th.M
                        ? ['MM', months]
                        : years <= 1
                          ? ['y']
                          : ['yy', years];

  const [key, value] = bucket;
  const phrase = DURATION_RELATIVE_TIME[key].replace('%d', String(value));
  if (!withSuffix) {
    return phrase;
  }
  // moment treats a zero/negative duration as past (`diff > 0 ? future : past`).
  const direction =
    ms > 0 ? DURATION_RELATIVE_TIME.future : DURATION_RELATIVE_TIME.past;
  return direction.replace('%s', phrase);
}

// The duration plugin is extended above; all Duration instances share one
// prototype, so patching it in place covers every `datetime.duration(...)`
// (including the shadow runner's dayjs side, which builds durations here).
Object.getPrototypeOf(dayjs.duration(0)).humanize = momentParityHumanize;

// Matches a trailing UTC offset in an ISO 8601 string: `+05:00`, `-0530`, `+08`.
// Groups: 1 = sign, 2 = hours, 3 = minutes (optional).
const ISO_OFFSET_SUFFIX = /([+-])(\d{2}):?(\d{2})?$/;

// === Moment-parity hardening for invalid dates (WORK-6090, M5.2 hotspot fix) ===
//
// The moment→dayjs shadow window flagged two real divergences in how the two
// libraries treat invalid input. moment is strict; dayjs is lenient. Cutover
// code that swaps `moment(x)` → `datetime(x)` must keep moment's contract or it
// silently changes behavior. The two fixes below restore parity. The one
// divergence we deliberately do NOT fix is the cosmetic `Invalid date` (moment)
// vs `Invalid Date` (dayjs) casing in `.format()` output — that is documented as
// an accepted benign quirk in the cutover PR.

// Matches a leading ISO calendar date (`YYYY-MM-DD`) followed by a date/time
// separator or end-of-string. Group 1 is the date portion only.
const ISO_DATE_PREFIX = /^(\d{4}-\d{2}-\d{2})(?:[T ]|$)/;

// moment rejects calendar-overflow dates (`2021-02-30`, `2021-13-45`,
// `0000-00-00`) as Invalid; dayjs's lenient parser silently rolls them into a
// real date (`2021-03-02`, `2022-02-14`, `1899-11-30`), so `.isValid()` returns
// true and `.valueOf()` returns a plausible-but-wrong timestamp. We restore
// moment's strictness by validating just the calendar-date portion with
// customParseFormat strict mode. Only the date part is checked: it is
// timezone-independent, so a genuinely valid instant carrying an offset/Z time
// component (e.g. `2024-06-08T23:30:00-08:00`, whose UTC date differs from its
// local date) is never false-flagged.
function hasOverflowDatePortion(input: string): boolean {
  const match = input.match(ISO_DATE_PREFIX);
  if (!match) {
    return false;
  }
  return !dayjs(match[1], 'YYYY-MM-DD', true).isValid();
}

// moment(<invalid>).toISOString() returns `null`; dayjs delegates to the native
// Date#toISOString(), which throws `RangeError: Invalid time value` on an
// invalid instant. Consumers migrated off moment expect the null, not an
// exception, so we restore moment's contract at the prototype level (covers
// every Dayjs instance, including those built via `.utc()` / `.tz()`). moment's
// own typings declare `toISOString(): string` while returning null at runtime
// for an invalid date; we mirror that exact typed-string / runtime-null shape.
const invalidToISOStringParity: PluginFunc = (_option, DayjsClass) => {
  const originalToISOString = DayjsClass.prototype.toISOString;
  DayjsClass.prototype.toISOString = function toISOString(
    this: DatetimeType
  ): string {
    return this.isValid()
      ? originalToISOString.call(this)
      : (null as unknown as string);
  };
};
dayjs.extend(invalidToISOStringParity);

// === Accepted divergence: `z` timezone-abbreviation token (WORK-6090, M5.2 — WORKS-33B) ===
//
// The shadow window flagged that dayjs's lowercase `z` token renders a `GMT±H`
// string (`GMT-3`) where moment-timezone renders the IANA abbreviation (`-03`,
// `IST`, …), and renders `zz` twice. We deliberately do NOT reproduce moment's
// form: matching it for every zone would mean shipping moment-timezone's
// abbreviation database inside the dayjs wrapper, and `GMT-3` is an accurate,
// unambiguous label that simply becomes the norm once moment is gone post-cutover.
// Recorded as an accepted quirk (option 3 — document-and-accept) in the cutover PR
// and Asana WORKS-33B, not fixed here.

// A real ISO 8601 zone designator (`+05:00`, `-0530`, `+08`) lives in the TIME
// portion, after the `T` (or space) that separates the date from the time.
// Scanning the whole string with ISO_OFFSET_SUFFIX false-matches a bare calendar
// date's own trailing `-DD` — `2026-06-28` reads as a bogus `-28:00` offset — which
// skips the zoneless→UTC branch in parseZone and renders a wrong instant: the
// scheduling/shifts shadow hotspot regression (WORK-6090/WORK-6365 Defect A;
// `moment.parseZone('2026-06-28')` is UTC). Restrict the offset match to the
// substring after the date so a date-only input is correctly treated as zoneless.
function isoOffsetMatch(input: string): RegExpMatchArray | null {
  const timeSeparator = input.search(/[T ]/);
  if (timeSeparator === -1) {
    return null;
  }
  return input.slice(timeSeparator).match(ISO_OFFSET_SUFFIX);
}

// True when an ISO string carries an explicit zone — a trailing `Z` or a real
// offset in its time portion. Mirrors moment's input-dependent zone handling: a
// zoneless string is read as UTC by parseZone and as wall-time-in-zone by tz,
// while a zone-bearing string keeps its own instant (WORK-6365).
function hasExplicitZone(input: string): boolean {
  return input.endsWith('Z') || isoOffsetMatch(input) !== null;
}

// === moment's fixed-offset mode, as a dayjs plugin ===
//
// `moment.parseZone('…-08:00')` produces an instance pinned to that offset: its
// display clock is independent of the host zone. dayjs has no such mode —
// `dayjs(input).utcOffset(n)` keeps a host-zone-backed Date and derives the
// display from it, so any wall clock the host zone does not have (a DST gap) is
// silently pushed forward, and `.format()` then disagrees with the instance's own
// `.valueOf()`.
//
// The fix models a fixed-offset instance as a UTC-mode dayjs positioned at the
// WALL CLOCK (`instant + offset`), with the offset stored at `$x.$fixedOffset`.
// UTC mode reads its display fields via `getUTC*`, so the host zone never
// participates. dayjs's internal wrapper copies `$x` into every derived instance,
// so `clone` / `add` / `subtract` / `set` / `startOf` carry the marker forward for
// free — the same extension point the official timezone plugin uses for
// `$x.$timezone`. `.utc()` / `.local()` / `.tz()` rebuild without `$x`, so the
// marker cannot leak out of the mode.
const MS_PER_MINUTE = 60_000;

// dayjs's plugin-extension bag. It is copied BY REFERENCE into every derived
// instance, so it may carry a mode field (a frozen offset, a zone name) but never
// per-instance state a derivation has to change: every builder below hands out a
// fresh bag rather than mutating one.
type DatetimeExtensionBag = {
  $fixedOffset?: number;
  $zoneAware?: string;
  $timezone?: string;
  $zoneOffset?: number;
};

// dayjs's private instance fields: `$d` is the underlying Date (the display clock
// in this mode, NOT the instant), `$L` the locale, `$x` the plugin-extension bag.
type FixedOffsetInstance = DatetimeType & {
  $d: Date;
  $L: string;
  $x?: DatetimeExtensionBag;
};

// `utcOffset` is overloaded across core and the utc plugin's augmentation, and
// `startOf`'s second parameter is internal and absent from the public typings, so
// the prototype has to be narrowed before these can be reassigned.
type FixedOffsetPrototype = {
  utcOffset: (
    input?: number | string,
    keepLocalTime?: boolean
  ) => number | DatetimeType;
  valueOf: () => number;
  format: (template?: string) => string;
  startOf: (unit: string, isStartOf?: boolean) => DatetimeType;
};

// The `(date, { utc, x, locale })` construction form is internal too — the public
// overloads only cover `(date, format, strict)`.
const buildDatetime = dayjs as unknown as (
  date: Date,
  config: { utc?: boolean; locale?: string; x?: DatetimeExtensionBag }
) => DatetimeType;

function fixedOffsetOf(instance: unknown): number | undefined {
  return (instance as FixedOffsetInstance).$x?.$fixedOffset;
}

// Offset 0 is plain UTC — keeping it there preserves moment's `Z` label rather
// than rendering `+00:00`.
function fixedOffsetDatetime(
  instantMs: number,
  offsetMinutes: number,
  locale?: string
): DatetimeType {
  if (offsetMinutes === 0) {
    return buildDatetime(new Date(instantMs), { utc: true, locale });
  }
  return buildDatetime(new Date(instantMs + offsetMinutes * MS_PER_MINUTE), {
    utc: true,
    locale,
    x: { $fixedOffset: offsetMinutes },
  });
}

const fixedOffsetMode: PluginFunc = (_option, DayjsClass) => {
  const proto = DayjsClass.prototype as unknown as FixedOffsetPrototype;
  const previousUtcOffset = proto.utcOffset;
  const previousValueOf = proto.valueOf;
  const previousFormat = proto.format;
  const previousStartOf = proto.startOf;

  // Rebuilds the instance without the fixed marker, so a previous implementation
  // sees an ordinary UTC-mode dayjs.
  const withoutMarker = (
    instance: FixedOffsetInstance,
    epochMs: number
  ): DatetimeType =>
    buildDatetime(new Date(epochMs), { utc: true, locale: instance.$L });

  proto.utcOffset = function utcOffset(
    this: FixedOffsetInstance,
    input?: number | string,
    keepLocalTime?: boolean
  ): number | DatetimeType {
    const offset = fixedOffsetOf(this);
    // The utc plugin's getter hard-returns 0 in UTC mode; the stored offset wins.
    // Only the GETTER needs the mode. The setter belongs to the zone-aware mode
    // registered below, which applies moment's rule to every instance alike, so
    // this reaches the previous implementation only for an invalid one.
    if (offset !== undefined && input === undefined) {
      return offset;
    }
    return previousUtcOffset.call(this, input, keepLocalTime);
  };

  // `$d` holds the display clock here, so the instant has to be derived rather
  // than read off it. Everything time-valued — `toISOString` / `toJSON` / `unix` /
  // `toDate` / `diff` / the comparison predicates — funnels through here.
  proto.valueOf = function valueOf(this: FixedOffsetInstance): number {
    const offset = fixedOffsetOf(this);
    if (offset === undefined) {
      return previousValueOf.call(this);
    }
    return this.$d.valueOf() - offset * MS_PER_MINUTE;
  };

  proto.format = function format(
    this: FixedOffsetInstance,
    template?: string
  ): string {
    const offset = fixedOffsetOf(this);
    // The utc plugin's default template embeds `[Z]` as a LITERAL, which would
    // print `Z` for a non-zero offset. Passing the template explicitly routes the
    // real `Z` token through `utcOffset()` and renders the actual label.
    if (offset !== undefined && !template) {
      return previousFormat.call(this, 'YYYY-MM-DDTHH:mm:ssZ');
    }
    return previousFormat.call(this, template);
  };

  // Core truncates via `toDate()` — the instant — which is exactly the value this
  // mode separates from the display clock. Truncate the display clock instead,
  // then re-stamp the offset. `endOf` delegates to `startOf(unit, false)`, so it
  // is covered by this one override.
  proto.startOf = function startOf(
    this: FixedOffsetInstance,
    unit: string,
    isStartOf?: boolean
  ): DatetimeType {
    const offset = fixedOffsetOf(this);
    if (offset === undefined) {
      return previousStartOf.call(this, unit, isStartOf);
    }
    const displayClock = withoutMarker(this, this.$d.valueOf());
    const truncated = previousStartOf.call(
      displayClock,
      unit,
      isStartOf
    ) as FixedOffsetInstance;
    return fixedOffsetDatetime(
      truncated.$d.valueOf() - offset * MS_PER_MINUTE,
      offset,
      this.$L
    );
  };
};
// Registered after `utc` and `timezone` so the overrides above wrap theirs.
dayjs.extend(fixedOffsetMode);

// === moment-timezone's zone-aware mode, as a dayjs plugin ===
//
// dayjs's `.tz()` is wrong about the offset in two independent ways.
//
// It derives the offset from `Date#toLocaleString` minus the HOST's
// `getTimezoneOffset()`, so its answer moves with the viewer's zone: the same
// conversion renders one wall clock in a UTC runner and another in a
// DST-observing one. And it FREEZES that offset at conversion time, where
// moment-timezone re-derives it from the zone on every operation — so
// `parseZone('2026-03-07T20:30:00-06:00').tz('America/Chicago').add(1,'day')`
// keeps `-06:00` and lands on a different instant than moment's `-05:00`.
//
// A zone-aware instance is the fixed-offset shape above with a zone tag: a
// UTC-mode dayjs whose `$d` holds the WALL CLOCK, with the zone and the resolved
// offset in `$x`. The offset comes from `Intl` and is re-resolved at every
// derivation intercepted below, so the host participates nowhere and the offset
// is never stale. `$offset` is left unset for the reason the fixed-offset mode
// leaves it unset: the utc plugin rewrites `toDate('s')` into a host-zone parse
// whenever it is truthy, and core's sub-day `startOf` routes through it.
//
// `$zoneAware` is the mode marker and stays distinct from `$timezone`, which the
// timezone plugin's `offsetName()` reads. The plugin's own static `dayjs.tz` tags
// instances with `$timezone` alone, and reading that as the marker would hand out
// an undefined offset.

// The timezone plugin's own offset algorithm, so a zone-aware instance and
// `offsetName()` can never disagree about what a zone does at an instant.
// Formatters are cached per zone: every derivation resolves at least one offset,
// and constructing an `Intl.DateTimeFormat` is orders of magnitude dearer than
// using one.
//
// What a derivation costs with the cache warm, measured under jsdom over 20k
// iterations: `add(1,'day')` 9.8 us/op against 2.0 for a plain instance,
// `startOf('day')` 7.8 against 1.0, `format()` 3.8. So five to eight times a
// plain derivation, each being one to three `formatToParts` calls plus a
// `dayjs.utc(string)` parse. Immaterial for ordinary rendering; worth knowing
// before deriving per cell across a large scheduling grid.
const zoneFormatters = new Map<string, Intl.DateTimeFormat>();

function zoneFormatter(zone: string): Intl.DateTimeFormat {
  const cached = zoneFormatters.get(zone);
  if (cached) {
    return cached;
  }
  const formatter = new Intl.DateTimeFormat('en-US', {
    hour12: false,
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  zoneFormatters.set(zone, formatter);
  return formatter;
}

// A zone's offset in minutes at an instant, read from `Intl` and never from the
// host: render the instant as the zone's wall clock, read that back as UTC, and
// the difference IS the offset.
function zoneOffsetAt(instantMs: number, zone: string): number {
  const fields: Record<string, number> = Object.fromEntries(
    zoneFormatter(zone)
      .formatToParts(new Date(instantMs))
      .map(({ type, value }) => [type, parseInt(value, 10)])
  );
  // `hour12: false` renders midnight as hour 24 in some engines.
  const hour = fields.hour === 24 ? 0 : fields.hour;
  const wallClock = `${fields.year}-${fields.month}-${fields.day} ${hour}:${fields.minute}:${fields.second}:000`;
  // Floor, not `% 1000`: the remainder carries the sign of the dividend, so
  // truncating toward zero disagrees with `Intl` by a second below the epoch and
  // the subtraction below then yields a non-integer offset — a pre-1970 instant
  // with milliseconds rendered as `-04:0.0166…`, malformed rather than merely
  // wrong. Same expression the corpus spec uses for its self-consistency check.
  const wholeSeconds = Math.floor(instantMs / 1000) * 1000;
  return (dayjs.utc(wallClock).valueOf() - wholeSeconds) / MS_PER_MINUTE;
}

// Wall clock → instant, resolving the two ways a wall clock can be ambiguous.
// In a spring-forward GAP the requested wall clock does not exist, so no offset
// settles and moment rolls forward past the transition; in a fall-back OVERLAP
// two instants share it and the first probe already names one. moment's rule:
// probe, re-probe at the candidate, and on disagreement take the larger offset.
function instantForWallClock(wallMs: number, zone: string): number {
  const probed = zoneOffsetAt(wallMs, zone);
  const candidate = wallMs - probed * MS_PER_MINUTE;
  const atCandidate = zoneOffsetAt(candidate, zone);
  if (atCandidate === probed) {
    return candidate;
  }
  const alternate = wallMs - atCandidate * MS_PER_MINUTE;
  if (zoneOffsetAt(alternate, zone) === atCandidate) {
    return alternate;
  }
  return wallMs - Math.min(probed, atCandidate) * MS_PER_MINUTE;
}

function buildZoneAware(
  wallMs: number,
  zone: string,
  offsetMinutes: number,
  locale?: string
): DatetimeType {
  return buildDatetime(new Date(wallMs), {
    utc: true,
    locale,
    x: { $zoneAware: zone, $timezone: zone, $zoneOffset: offsetMinutes },
  });
}

// Positions an INSTANT in a zone. The offset is resolved at that instant, so both
// passes of a fall-back hour come out right by construction.
function zoneAwareDatetime(
  instantMs: number,
  zone: string,
  locale?: string
): DatetimeType {
  const offset = zoneOffsetAt(instantMs, zone);
  return buildZoneAware(
    instantMs + offset * MS_PER_MINUTE,
    zone,
    offset,
    locale
  );
}

// Positions a WALL CLOCK in a zone. `preferredOffset` is the offset the instance
// being derived from already carries, and honoring it while it is still valid at
// the new wall clock is part of the contract rather than a shortcut: on the
// second 01:30 of a fall-back day both offsets are valid there, and re-resolving
// from scratch would silently move `startOf('hour')` onto the first pass.
function zoneAwareFromWallClock(
  wallMs: number,
  zone: string,
  locale?: string,
  preferredOffset?: number
): DatetimeType {
  if (
    preferredOffset !== undefined &&
    zoneOffsetAt(wallMs - preferredOffset * MS_PER_MINUTE, zone) ===
      preferredOffset
  ) {
    return buildZoneAware(wallMs, zone, preferredOffset, locale);
  }
  return zoneAwareDatetime(instantForWallClock(wallMs, zone), zone, locale);
}

// Units as dayjs normalizes them (`d`/`days` → `day`, `M`/`months` → `month`, …).
// moment moves the wall clock for these and the instant for everything else, and
// across a transition the two land an hour apart.
const CALENDAR_UNITS = new Set(['date', 'day', 'week', 'month', 'year']);

// The utc plugin's own offset grammar and its `<= 16 means hours` convention,
// replicated so `utcOffset(n)` accepts exactly what it accepted before: an
// unparseable string leaves the instance alone rather than invalidating it.
const UTC_OFFSET_STRING = /[+-]\d\d(?::?\d\d)?/;
const UTC_OFFSET_PARTS = /([+-]|\d\d)/g;

function offsetMinutesFromString(input: string): number | null {
  const match = input.match(UTC_OFFSET_STRING);
  if (!match) {
    return null;
  }
  const parts = match[0].match(UTC_OFFSET_PARTS) ?? ['-', '0', '0'];
  const magnitude = 60 * Number(parts[1]) + Number(parts[2]);
  if (magnitude === 0) {
    return 0;
  }
  return parts[0] === '+' ? magnitude : -magnitude;
}

type ZoneAwareBag = {
  $zoneAware: string;
  $timezone: string;
  $zoneOffset: number;
};

// The mode marker and the resolved offset are written together by
// `buildZoneAware`, so the presence of the marker narrows the whole bag.
function zoneAwareBag(instance: FixedOffsetInstance): ZoneAwareBag | undefined {
  const bag = instance.$x;
  return bag?.$zoneAware === undefined ? undefined : (bag as ZoneAwareBag);
}

// `add`'s and `set`'s unit arguments are typed as dayjs's own unit unions and
// `startOf`'s second parameter is internal, so the prototype has to be narrowed
// before these can be reassigned.
type ZoneAwarePrototype = {
  utcOffset: (
    input?: number | string,
    keepLocalTime?: boolean
  ) => number | DatetimeType;
  valueOf: () => number;
  format: (template?: string) => string;
  startOf: (unit: string, isStartOf?: boolean) => DatetimeType;
  add: (value: number, unit?: string) => DatetimeType;
  set: (unit: string, value: number) => DatetimeType;
  tz: (zone?: string, keepLocalTime?: boolean) => DatetimeType;
  diff: (input?: unknown, unit?: string, asFloat?: boolean) => number;
  isUTC: () => boolean;
  $utils: () => { p: (unit?: string) => string };
};

const zoneAwareMode: PluginFunc = (_option, DayjsClass) => {
  const proto = DayjsClass.prototype as unknown as ZoneAwarePrototype;
  const previousUtcOffset = proto.utcOffset;
  const previousValueOf = proto.valueOf;
  const previousFormat = proto.format;
  const previousStartOf = proto.startOf;
  const previousAdd = proto.add;
  const previousSet = proto.set;
  const previousTz = proto.tz;
  const previousDiff = proto.diff;
  // dayjs's own unit normalizer, so this mode's unit families and core's unit
  // dispatch cannot disagree. It reads no state, hence the unbound reference.
  const normalizeUnit = proto.$utils().p;

  // The wall clock as an ordinary UTC-mode instance. It carries no `$x`, so every
  // override here early-returns on it and the previous implementations see plain
  // dayjs. That is what stops a double derivation: core's `add` reaches month and
  // year through `this.set(...)` and day and week through `.date(...)`, both of
  // which re-enter this prototype.
  const bareWallClock = (instance: FixedOffsetInstance): DatetimeType =>
    buildDatetime(new Date(instance.$d.valueOf()), {
      utc: true,
      locale: instance.$L,
    });

  proto.utcOffset = function utcOffset(
    this: FixedOffsetInstance,
    input?: number | string,
    keepLocalTime?: boolean
  ): number | DatetimeType {
    const bag = zoneAwareBag(this);
    // The utc plugin's getter hard-returns 0 in UTC mode; the resolved offset wins.
    if (input === undefined) {
      return bag === undefined ? previousUtcOffset.call(this) : bag.$zoneOffset;
    }
    if (!this.isValid()) {
      return previousUtcOffset.call(this, input, keepLocalTime);
    }
    // The SETTER deliberately covers every instance, not just this mode. The utc
    // plugin implements it as `this.local().add(offset + localTimezoneOffset)` and
    // freezes `$x.$localOffset` from the PRE-move host offset, so any move across a
    // host DST boundary leaves `format()` contradicting the instance's own
    // `valueOf()` — under TZ=America/Denver,
    // `parseZone('2027-03-14T00:00:00-08:00').utcOffset(-300)` rendered
    // `04:00-05:00` while holding 03:00-05:00's instant. Scoping the fix to the
    // two local modes would not be enough: `parseZone` hands back a plain UTC-mode
    // instance for `Z`-suffixed and zoneless input, and the plugin's setter is
    // broken for those too.
    //
    // moment's rule instead — keep the instant (or the wall clock, with
    // `keepLocalTime`), stamp the offset, and never consult the host. The result is
    // a fixed-offset instance in every case, so `utcOffset(n)` on a zone-aware
    // instance drops the zone exactly as it does in moment.
    const requested =
      typeof input === 'string' ? offsetMinutesFromString(input) : input;
    if (requested === null) {
      return this;
    }
    const minutes = Math.abs(requested) <= 16 ? requested * 60 : requested;
    if (keepLocalTime) {
      const wallClock = dayjs
        .utc(this.format('YYYY-MM-DDTHH:mm:ss.SSS'))
        .valueOf();
      return fixedOffsetDatetime(
        wallClock - minutes * MS_PER_MINUTE,
        minutes,
        this.$L
      );
    }
    return fixedOffsetDatetime(this.valueOf(), minutes, this.$L);
  };

  // `$d` holds the wall clock here, so the instant has to be derived from it.
  proto.valueOf = function valueOf(this: FixedOffsetInstance): number {
    const bag = zoneAwareBag(this);
    if (bag === undefined) {
      return previousValueOf.call(this);
    }
    return this.$d.valueOf() - bag.$zoneOffset * MS_PER_MINUTE;
  };

  proto.format = function format(
    this: FixedOffsetInstance,
    template?: string
  ): string {
    const bag = zoneAwareBag(this);
    // moment renders a bare `Z` whenever the RESOLVED offset is zero — London in
    // winter, a `UTC` target — and `+01:00` otherwise. This mode cannot
    // short-circuit to `dayjs.utc` the way the fixed-offset mode does, because
    // that would drop the zone tag `offsetName()` reads, so the template is
    // chosen by the offset instead: delegating with none leaves the utc plugin's
    // literal `[Z]` default in place, which is exactly right at zero.
    if (bag !== undefined && !template && bag.$zoneOffset !== 0) {
      return previousFormat.call(this, 'YYYY-MM-DDTHH:mm:ssZ');
    }
    return previousFormat.call(this, template);
  };

  // Core truncates through `toDate()` — the instant — and the timezone plugin's
  // own override ends in `.tz(zone, true)`, inheriting the offset bug. Truncate
  // the wall clock and re-derive. `endOf` is `startOf(unit, false)`, so this one
  // override covers it.
  proto.startOf = function startOf(
    this: FixedOffsetInstance,
    unit: string,
    isStartOf?: boolean
  ): DatetimeType {
    const bag = zoneAwareBag(this);
    if (bag === undefined) {
      return previousStartOf.call(this, unit, isStartOf);
    }
    const truncated = previousStartOf.call(
      bareWallClock(this),
      unit,
      isStartOf
    ) as FixedOffsetInstance;
    return zoneAwareFromWallClock(
      truncated.$d.valueOf(),
      bag.$zoneAware,
      this.$L,
      bag.$zoneOffset
    );
  };

  // `subtract` is `add(-n, unit)` in core, so it is covered here too.
  proto.add = function add(
    this: FixedOffsetInstance,
    value: number,
    unit?: string
  ): DatetimeType {
    const bag = zoneAwareBag(this);
    if (bag === undefined) {
      return previousAdd.call(this, value, unit);
    }
    const advanced = previousAdd.call(
      bareWallClock(this),
      value,
      unit
    ) as FixedOffsetInstance;
    if (CALENDAR_UNITS.has(normalizeUnit(unit))) {
      return zoneAwareFromWallClock(
        advanced.$d.valueOf(),
        bag.$zoneAware,
        this.$L,
        bag.$zoneOffset
      );
    }
    return zoneAwareDatetime(
      this.valueOf() + (advanced.$d.valueOf() - this.$d.valueOf()),
      bag.$zoneAware,
      this.$L
    );
  };

  // Every field setter (`.hour(5)`, `.date(1)`, …) routes through core's `$g` to
  // `set`, so this one override covers them all.
  proto.set = function set(
    this: FixedOffsetInstance,
    unit: string,
    value: number
  ): DatetimeType {
    const bag = zoneAwareBag(this);
    if (bag === undefined) {
      return previousSet.call(this, unit, value);
    }
    const updated = previousSet.call(
      bareWallClock(this),
      unit,
      value
    ) as FixedOffsetInstance;
    return zoneAwareFromWallClock(
      updated.$d.valueOf(),
      bag.$zoneAware,
      this.$L,
      bag.$zoneOffset
    );
  };

  // Applies to EVERY instance, not just this mode: replacing the plugin's
  // host-derived conversion is the fix, so there is nothing to delegate to.
  proto.tz = function tz(
    this: FixedOffsetInstance,
    zone?: string,
    keepLocalTime?: boolean
  ): DatetimeType {
    // `parseZone(null).tz(zone)` must stay an Invalid date rather than throw:
    // resolving an offset for `NaN` throws a RangeError out of `Intl`.
    if (!this.isValid()) {
      return previousTz.call(this, zone, keepLocalTime);
    }
    const target = zone ?? dayjs.tz.guess();
    if (keepLocalTime) {
      return zoneAwareFromWallClock(
        dayjs.utc(this.format('YYYY-MM-DDTHH:mm:ss.SSS')).valueOf(),
        target,
        this.$L
      );
    }
    return zoneAwareDatetime(this.valueOf(), target, this.$L);
  };

  // dayjs builds the comparand with `dayjs(input)` — local mode — and then, for
  // the day, week, month and year units, subtracts a delta built from that
  // comparand's HOST offset. So the same two instants can differ by a day
  // depending only on where the viewer sits, even though neither operand is
  // local: `parseZone('2026-06-15T09:00:00+05:30').tz('Australia/Sydney', true)`
  // measures 164 days from 2026-01-01Z under a UTC viewer and 165 under New
  // York. The hour unit is unaffected, which is why this hid for so long.
  //
  // moment compares in the receiver's own frame — `cloneWithOffset` repositions
  // the comparand to the model's offset before subtracting, so the delta is zero
  // and the host never participates. Do the same: rebuild the comparand at its
  // own instant in this instance's zone (re-deriving the offset there, which is
  // what moment-timezone's updateOffset does) or at its fixed offset.
  proto.diff = function diff(
    this: FixedOffsetInstance,
    input?: unknown,
    unit?: string,
    asFloat?: boolean
  ): number {
    // The condition is UTC mode, not the presence of a mode marker. All three
    // receiver shapes are UTC-mode instances whose offset is not the host's, and
    // the marker-less one is the common case rather than an edge: `parseZone`
    // returns a bare `dayjs.utc` for `Z`-suffixed and zoneless input, and a zero
    // offset collapses to the same thing. Gating on the marker fixed the two
    // shapes that carry one and left the dominant one behind.
    if (!this.isUTC()) {
      return previousDiff.call(this, input, unit, asFloat);
    }
    const instant = dayjs(input as dayjs.ConfigType).valueOf();
    if (Number.isNaN(instant)) {
      return previousDiff.call(this, input, unit, asFloat);
    }
    const bag = zoneAwareBag(this);
    const fixed = fixedOffsetOf(this);
    const comparand =
      bag === undefined
        ? fixedOffsetDatetime(instant, fixed ?? 0, this.$L)
        : zoneAwareDatetime(instant, bag.$timezone, this.$L);
    return previousDiff.call(this, comparand, unit, asFloat);
  };
};
// Registered after `fixedOffsetMode` so these overrides wrap its, which wrap the
// utc and timezone plugins'. The `dayjs.extend` calls at the top of the file must
// keep `advancedFormat` BEFORE `utc`: advancedFormat also overrides `format` and
// defaults to core's `'YYYY-MM-DDTHH:mm:ssZ'`, which shadows the utc plugin's
// literal `[Z]` default and turns every offset-zero render into `+00:00`.
dayjs.extend(zoneAwareMode);

export function parseZone(input: string | DatetimeType): DatetimeType {
  // A real datetime passes through unchanged. Any OTHER non-string
  // (`null`, `NaN`, junk) becomes an Invalid date, matching
  // `moment.parseZone(null)` — which formats to "Invalid date". Without this
  // guard dayjs let `null` fall through and the chained `.tz()` threw a
  // TypeError, crashing the scheduling/shifts "Last Updated" column on cutover
  // (WORK-6090, WORKS-36S; Sentry 515b126d). Deliberate divergence: Moment is
  // NOT uniformly lenient — `moment.parseZone(undefined) === now` and
  // `moment.parseZone(<number>) === epoch` — whereas this broad guard returns
  // Invalid for those too. Both are `string | DatetimeType` type-contract
  // violations, do not occur for the scheduling GraphQL fields, and would
  // already throw under dayjs, so returning Invalid is strictly safer than the
  // old passthrough.
  if (isDatetime(input)) {
    return input;
  }
  if (typeof input !== 'string') {
    return dayjs(NaN);
  }

  // Reject calendar-overflow dates up front so parseZone matches moment.parseZone,
  // which returns an Invalid date for these rather than rolling them over.
  if (hasOverflowDatePortion(input)) {
    return dayjs(NaN);
  }

  if (input.endsWith('Z')) {
    return dayjs.utc(input);
  }

  const match = isoOffsetMatch(input);
  // A zoneless datetime (no trailing `Z`, no `±HH:MM` suffix) must be read as
  // UTC to match `moment.parseZone`, which assigns offset 0 to an offset-less
  // input. `dayjs(input)` would parse it in the browser's LOCAL zone instead —
  // same wall clock, different instant — which surfaced as the scheduling/shifts
  // shadow hotspot (WORK-6090: WORKS-33D/E/F, `moment.parseZone(shift.startDatetime)`
  // when the datetime arrived without an offset). Use `dayjs.utc` so the zone, and
  // therefore every chained `.add`/`.date`/`.format`, matches moment.
  if (!match) {
    return dayjs.utc(input);
  }

  const sign = match[1] === '+' ? 1 : -1;
  const hours = parseInt(match[2], 10);
  const minutes = parseInt(match[3] || '0', 10);
  const totalMinutes = sign * (hours * 60 + minutes);

  // Deterministic construction, no native `Date.parse`: the wall-clock portion
  // read as UTC IS the display clock, and the instant is that minus the offset.
  const wallClock = input.slice(0, input.length - match[0].length);
  const displayMs = dayjs.utc(wallClock).valueOf();
  return fixedOffsetDatetime(
    displayMs - totalMinutes * MS_PER_MINUTE,
    totalMinutes
  );
}

export function isDatetime(value: unknown): value is DatetimeType {
  return dayjs.isDayjs(value);
}

// moment tags its instances with `_isAMomentObject` — the same marker
// `moment.isMoment` checks. We duck-type on it so this file never imports
// moment (keeping the moment island confined to moment-bridge.ts).
function isMomentObject(
  value: unknown
): value is { format: (template?: string) => string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { _isAMomentObject?: boolean })._isAMomentObject === true
  );
}

// A *local-mode* moment (built by `moment(input)` with no zone conversion)
// recomputes its UTC offset whenever arithmetic moves it across a DST boundary —
// `moment.isLocal()` is true only for that mode and false for UTC-mode
// (`moment.utc(...)`) and fixed-offset (`moment.parseZone(...)` / `.utcOffset(n)`)
// moments, whose offset is intentionally frozen. Duck-typed on the public
// `isLocal()` method like isMomentObject so this file never imports moment; a
// stub without `isLocal` (the parity specs' `momentLike`) is treated as
// non-local and keeps the offset-preserving round-trip path.
function isLocalModeMoment(value: unknown): boolean {
  if (!isMomentObject(value)) {
    return false;
  }
  const probe = value as unknown as { isLocal?: () => boolean };
  return typeof probe.isLocal === 'function' && probe.isLocal();
}

// Reads the absolute instant off a (duck-typed) moment object for the local-mode
// construction path — paired with isLocalModeMoment, never called on its own.
function momentInstant(value: {
  format: (template?: string) => string;
}): number {
  return (value as unknown as { valueOf: () => number }).valueOf();
}

// moment's default `.format()` emits ISO 8601 *without* milliseconds, so
// round-tripping a Moment object through it silently truncates sub-second
// precision: scheduling/shifts WORKS-333 — `moment(currentDate).valueOf()` kept
// `…038` while the dayjs equivalent landed on the whole second (`…000`). Round-
// trip through an explicit millisecond-precision ISO format instead; its `Z`
// token still carries the offset, so both the zone (the #7198 fix) and the
// milliseconds survive.
const MOMENT_ROUNDTRIP_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

// moment's own isDate check (realm-safe, matches moment.isDate). A Date passed
// with a format argument needs the same format-ignoring treatment as a moment
// object — moment uses the instant and ignores the format for both.
function isDateObject(value: unknown): value is Date {
  return Object.prototype.toString.call(value) === '[object Date]';
}

// True when no construction args beyond the first were supplied. The shadow
// factory always calls `datetime(input, format, strict)` with the trailing two
// `undefined`, so a plain `rest.length === 0` check would miss the bare
// single-arg call shape coming through that path.
function hasNoExtraArgs(rest: unknown[]): boolean {
  return rest.every((arg) => arg === undefined);
}

// Bare construction: `datetime('2021-02-30')` returns an Invalid date like
// moment, instead of dayjs's silent rollover. Only a single bare string arg is
// intercepted; explicit-format calls (`datetime(str, fmt)`) keep dayjs's
// caller-controlled parsing.
function createDatetime(
  date?: dayjs.ConfigType,
  ...rest: unknown[]
): DatetimeType {
  // A *moment* object (not a dayjs one) handed to datetime() — e.g. an
  // incremental cutover where an upstream module still produces moments.
  // moment(m) clones m preserving its zone mode, so a UTC-mode moment renders
  // in UTC (`2026-05-31T15:07:40Z`); dayjs(m), seeing a foreign object, only
  // reads m.valueOf() and falls back to LOCAL, silently shifting the displayed
  // offset (`2026-05-31T11:07:40-04:00`) — the scheduling/shifts shadow hotspot
  // (WORK-6090). Round-trip through the moment's own offset- and millisecond-
  // preserving ISO string so the zone and sub-second precision survive, exactly
  // as momentToDayjs() does. dayjs objects fall through: dayjs(dayjsObj) already
  // clones and preserves the zone.
  //
  // The format/strict args are intentionally ignored here: moment consults a
  // format ONLY for string input, so `moment(m, 'HH:mm')` clones m and ignores
  // the format just like the bare `moment(m)`. (Originally gated on
  // `hasNoExtraArgs`; ungated to also cover the `moment(m, fmt)` call shape —
  // the residual scheduling/shifts divergence #7198 left, where dayjs's
  // customParseFormat ran on the object and returned an Invalid date.)
  if (isMomentObject(date)) {
    // A local-mode moment must become a zone-aware (local) dayjs, not a frozen-
    // offset one. parseZone(m.format()) bakes in the moment's *current* offset, so
    // later month/day arithmetic that crosses a DST boundary keeps the stale
    // offset where the local moment recomputes it — the scheduling/shifts
    // divergence (WORK-6090, WORKS-340/341): a June-EDT base subtract(1,"month")'d
    // (or startOf("month")'d) into EST rendered `-04:00` where moment recomputes
    // `-05:00`. dayjs(instant) renders in the same system zone as the local
    // moment, so the display matches at construction AND recomputes under
    // arithmetic; valueOf() keeps the milliseconds (WORKS-333). UTC- and
    // fixed-offset moments keep the offset-preserving round-trip below — their
    // offset is meant to stay frozen (WORKS-33D/E/F).
    if (isLocalModeMoment(date)) {
      return (dayjs as (...args: unknown[]) => DatetimeType)(
        momentInstant(date)
      );
    }
    return parseZone(date.format(MOMENT_ROUNDTRIP_FORMAT));
  }
  // A *Date* object handed in *with* a format/strict argument: moment ignores
  // the format for Date input and uses the instant, but dayjs's
  // customParseFormat runs on the Date and returns an Invalid date. Drop the
  // extra args and let dayjs parse the Date alone, matching moment (WORK-6090,
  // residual of #7198). Scoped to Date (plus the Moment object above): moment
  // does NOT blanket-ignore the format for other inputs — `moment(<number>,
  // 'HH:mm')` is itself Invalid and dayjs already agrees, so numbers / foreign
  // objects keep the caller's args and stay on the default path.
  if (!hasNoExtraArgs(rest) && isDateObject(date)) {
    return (dayjs as (...args: unknown[]) => DatetimeType)(date);
  }
  if (
    hasNoExtraArgs(rest) &&
    typeof date === 'string' &&
    hasOverflowDatePortion(date)
  ) {
    return dayjs(NaN);
  }
  return (dayjs as (...args: unknown[]) => DatetimeType)(date, ...rest);
}

// `datetime.utc('2021-02-30')` mirrors `moment.utc(...)` strictness too, since
// cutover maps `moment.utc(x)` → `datetime.utc(x)`.
const createUtcDatetime = ((
  date?: dayjs.ConfigType,
  ...rest: unknown[]
): DatetimeType => {
  // Mirror createDatetime's object-input parity on the UTC path: moment.utc
  // ignores a format argument for a Moment / Date input and keeps the instant
  // (in UTC mode), but dayjs.utc's customParseFormat returns an Invalid date. A
  // moment object round-trips through its zone-preserving string and is then
  // converted to UTC, so an offset moment's instant survives
  // (`moment.utc(parseZone('…-05:00'))` → the equivalent `…Z`); a Date uses its
  // instant directly. Numbers / foreign objects keep the caller's args — moment
  // does not blanket-ignore the format for them, and dayjs already agrees
  // (WORK-6090, residual of #7198).
  if (isMomentObject(date)) {
    // Same local-mode parity as createDatetime: a local moment yields a
    // zone-aware dayjs (from the instant) before `.utc()`, so the underlying
    // instant — and any later DST-crossing arithmetic — matches moment.utc(m)
    // (WORK-6090, WORKS-340/341). UTC/fixed-offset moments keep the round-trip.
    if (isLocalModeMoment(date)) {
      return dayjs(momentInstant(date)).utc();
    }
    return parseZone(date.format(MOMENT_ROUNDTRIP_FORMAT)).utc();
  }
  if (!hasNoExtraArgs(rest) && isDateObject(date)) {
    return dayjs.utc(date);
  }
  if (
    rest.length === 0 &&
    typeof date === 'string' &&
    hasOverflowDatePortion(date)
  ) {
    return dayjs.utc(NaN);
  }
  return (dayjs.utc as (...args: unknown[]) => DatetimeType)(date, ...rest);
}) as typeof dayjs.utc;

// moment-parity for `moment.tz(input, zone)` (moment-timezone). moment reads a
// ZONELESS string as wall-time IN `zone`, but honors an explicit offset/`Z` and
// uses the absolute instant of a Date/number/Moment. Each bare dayjs primitive
// matches only half of that: `dayjs.tz(input, zone)` reads wall-time as `zone`
// (right for zoneless) but ignores an explicit offset; `dayjs(input).tz(zone)`
// honors the instant (right for zone-bearing / non-string) but reads a zoneless
// string in the RUNNER's zone. The shadow's tz side used the latter, so
// `moment.tz(zonelessShiftDatetime, shiftZone)` diverged for every viewer whose
// runner zone differed from the shift's zone — the scheduling/shifts regression
// (WORK-6365 Defect B). Branch on the input to match moment across both. Cutover
// maps `moment.tz(x, z)` → `datetime.tz(x, z)`, so the fix lives in the wrapper.
const createTzDatetime = Object.assign(
  ((input?: dayjs.ConfigType, zone?: string): DatetimeType => {
    if (
      typeof input === 'string' &&
      zone !== undefined &&
      !hasExplicitZone(input)
    ) {
      // The plugin's *static* never routes a string through the instance `.tz()`
      // the zone-aware mode replaces: it seeds its gap/overlap resolution with the
      // offset of NOW in the target zone and finishes with the host-repositioning
      // primitive, so `datetime.tz('2026-03-08T02:30:00', 'America/New_York')`
      // renders 03:30 in a UTC runner and 04:30 in a Los Angeles one. Read the
      // wall clock as UTC and position it in the zone directly.
      const wallClock = dayjs.utc(input);
      if (!wallClock.isValid()) {
        return wallClock;
      }
      return zoneAwareFromWallClock(wallClock.valueOf(), zone);
    }
    return createDatetime(input).tz(zone as string);
  }) as typeof dayjs.tz,
  // Preserve dayjs.tz's own statics (`guess`, `setDefault`) on the override.
  dayjs.tz
);

export const datetime = Object.assign(createDatetime, dayjs, {
  utc: createUtcDatetime,
  tz: createTzDatetime,
}) as typeof dayjs;

// Today's date as an ISO `YYYY-MM-DD` string in the active timezone. Prefer this over
// `new Date().toISOString().slice(0, 10)`, which is UTC-based and drifts across the day
// boundary in non-UTC zones.
export const todayIso = (): string => datetime().format('YYYY-MM-DD');

export type { DatetimeType };
